import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { MainAppReducer } from 'Types';
import { NotifyPhaserEvents } from 'Phaser/EventBus2';
import {
  DEFAULT_COMPLEXITY_SETTINGS,
  coerceComplexitySettings,
  complexitySettingsToTierConfig,
  type ComplexitySettings,
} from './ComplexitySettings';

/**
 * Reads the complexity settings from the store and writes changes back to it.
 *
 * The store owns them: it restores them from localStorage on load and writes
 * through on every change, so this hook holds no state of its own and several
 * mounts of the settings panel cannot disagree.
 *
 * Forwarding to Phaser happens here rather than in the reducer, which has to
 * stay pure. It fires on mount as well as on change, because the controller is
 * built when the scene starts — either side of this mounting — and sending only
 * on change would leave a controller created afterwards running defaults while
 * the panel showed something else.
 */
export function useComplexitySettings(): {
  settings: ComplexitySettings;
  update: (patch: Partial<ComplexitySettings>) => void;
  reset: (keys?: ReadonlyArray<keyof ComplexitySettings>) => void;
} {
  const dispatch = useDispatch();
  const stored = useSelector((state: MainAppReducer) => state.mainAppReducer.complexitySettings);
  // Coerced on the way out as well as in: redux-persist rehydrates with
  // `hardSet`, so a slice written by an older build lands in the store without
  // passing through the reducer's own validation. Memoised on the stored object
  // so the forwarding effect below sees a stable reference.
  const settings = useMemo(() => coerceComplexitySettings(stored), [stored]);

  const update = useCallback(
    (patch: Partial<ComplexitySettings>) => {
      dispatch(appActions.setComplexitySettings(patch));
    },
    [dispatch],
  );

  /** Resets the named settings, or all of them when none are named. */
  const reset = useCallback(
    (keys?: ReadonlyArray<keyof ComplexitySettings>) => {
      if (!keys) {
        dispatch(appActions.setComplexitySettings({ ...DEFAULT_COMPLEXITY_SETTINGS }));
        return;
      }
      const patch: Partial<ComplexitySettings> = {};
      keys.forEach((key) => {
        // Safe: key and value are drawn from the same field of the same type.
        (patch as Record<string, unknown>)[key] = DEFAULT_COMPLEXITY_SETTINGS[key];
      });
      dispatch(appActions.setComplexitySettings(patch));
    },
    [dispatch],
  );

  useEffect(() => {
    NotifyPhaserEvents.UpdateComplexityConfig({
      config: complexitySettingsToTierConfig(settings),
    });
  }, [settings]);

  return { settings, update, reset };
}
